import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Lock } from 'lucide-react';
import { Card } from '@components/base/layout';
import { Text, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Alert } from '@components/base/feedback';
import { EntityForm, useEntityForm } from '@components/integrated/entity-form';
import type {
  EntityFormConfig,
  FieldConfig,
  SelectFieldOptions,
} from '@components/integrated/entity-form';
import {
  applyLayoutOverrides,
  buildPublishedConfigFromEntityForm,
  resolveCardTitle,
  usePublishLayoutConfig,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import { clotureService } from '../api/clotureService';
import type { MotifClotureOption } from '../api/clotureService';
import type { WorkflowExecutionError } from '../api/taskService';
import { actionAnchorId } from '../configs/otherActionsConfig';
import { WorkflowStepTracker } from './WorkflowStepTracker';
import type { WorkflowStepExecutionSummary } from './WorkflowStepTracker';

interface ClotureSinistreCardProps {
  /** Claim id (`ID_SIN`) — sent (as a number) to the `claimflow-close-claim` workflow. */
  idSin: string;
  /**
   * Called once the claim is closed. Wired by the page (`TaskDetailPage` →
   * `refetch()`) so the Résumé status (`LIB_MVT`) refreshes — a runtime
   * callback, not a registered (tenant-tunable) prop.
   */
  onClaimClosed?: () => void;
  /**
   * Tenant-scoped layout overrides, injected by the `withComponentRules` HOC
   * (do not pass by hand). With no rules authored this is `undefined` and the
   * render is the baseline. Its `fields` bucket targets the stable field key
   * `motif`, `actions.submit` the « Valider » button and `title` the card
   * title — merged through `applyLayoutOverrides` / `resolveCardTitle`.
   */
  _layoutOverrides?: LayoutOverrides;
}

// ---------------------------------------------------------------------------
// Declarative config — stable key, label / placeholder / tooltip live here
// ---------------------------------------------------------------------------

const CLOTURE_SINISTRE_COMPONENT_ID = 'claimflow.clotureSinistreCard';

/** Stable key of the « Motif » select — its value is the chosen `CD_MOT`. */
const MOTIF_FIELD_KEY = 'motif';

/** Runtime texts of the card (never inline JSX strings). */
const CLOTURE_SINISTRE_TEXT = {
  title: 'Clôture',
  submit: 'Valider',
  /** Disabled button + header recap once the claim is closed. */
  submitted: 'Sinistre clôturé',
  /** « Motif » select placeholder while the motifs load. */
  optionsLoadingPlaceholder: 'Chargement des motifs…',
  optionsLoadErrorTitle: 'Impossible de charger les motifs de clôture',
  optionsLoadErrorFallback: 'Le chargement des motifs de clôture a échoué.',
  closeErrorTitle: 'La clôture a échoué',
  closeErrorFallback: 'La clôture du sinistre a échoué.',
  /** Guard — the button is disabled without a motif, so this never shows in practice. */
  motifMissing: 'Sélectionnez un motif de clôture.',
  stepsHeading: "Détail de l'exécution :",
} as const;

/**
 * The card's single field (mahhp 22846 mockup): the closure motif, required —
 * the workflow rejects an empty one. Option items are injected by
 * {@link buildClotureSinistreFormConfig}.
 */
const CLOTURE_SINISTRE_FIELDS: Record<string, FieldConfig> = {
  [MOTIF_FIELD_KEY]: {
    type: 'select',
    label: 'Motif',
    order: 1,
    tooltip:
      'Motif de clôture enregistré sur le dossier — le sinistre passe au mouvement « CLOSIN ».',
    validation: { required: true },
    options: { searchable: true, placeholder: 'Sélectionnez un motif' } as SelectFieldOptions,
  },
};

function buildClotureSinistreFormConfig(motifs: MotifClotureOption[]): EntityFormConfig {
  const fields: Record<string, FieldConfig> = { ...CLOTURE_SINISTRE_FIELDS };
  const motifField = fields[MOTIF_FIELD_KEY];
  if (motifField) {
    fields[MOTIF_FIELD_KEY] = {
      ...motifField,
      options: {
        ...(motifField.options as SelectFieldOptions | undefined),
        items: motifs.map((motif) => ({ value: motif.code, label: motif.label })),
      } as SelectFieldOptions,
    };
  }

  return {
    id: 'cloture-sinistre',
    endpoint: {
      erpEndpoint: '/claimflow/workflows/claimflow-close-claim/execute',
      method: 'POST',
    },
    fields,
    actions: {
      submit: { visible: true, label: CLOTURE_SINISTRE_TEXT.submit },
      cancel: { visible: false },
      reset: { visible: false },
    },
    layout: { columns: 1 },
  };
}

type OptionsLoadState = 'loading' | 'ready' | 'error';

/**
 * Overlay the runtime state on the (override-merged) config: the « Motif »
 * select is disabled with a loading placeholder while the motifs load,
 * disabled once their fetch failed (the error shows in an `Alert`), and
 * disabled after the claim is closed (read-only recap of the chosen motif).
 */
function withRuntimeState(
  config: EntityFormConfig,
  optionsState: OptionsLoadState,
  submitted: boolean,
): EntityFormConfig {
  const field = config.fields[MOTIF_FIELD_KEY];
  if (!field || (optionsState === 'ready' && !submitted)) return config;

  let next: FieldConfig;
  if (optionsState === 'loading') {
    next = {
      ...field,
      disabled: true,
      options: {
        ...(field.options as SelectFieldOptions | undefined),
        placeholder: CLOTURE_SINISTRE_TEXT.optionsLoadingPlaceholder,
      } as SelectFieldOptions,
    };
  } else {
    next = { ...field, disabled: true };
  }
  return { ...config, fields: { ...config.fields, [MOTIF_FIELD_KEY]: next } };
}

/** A non-empty motif code, `null` otherwise. */
function toMotifCode(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** The selected `CD_MOT` from the built payload, `null` when none. */
function readMotif(payload: Record<string, unknown> | FormData): string | null {
  return toMotifCode(
    payload instanceof FormData ? payload.get(MOTIF_FIELD_KEY) : payload[MOTIF_FIELD_KEY],
  );
}

/** Step trail of a failed workflow run (see `WorkflowExecutionError`). */
function readStepExecutions(error: unknown): WorkflowStepExecutionSummary[] {
  if (!(error instanceof Error) || !('stepExecutions' in error)) return [];
  const steps = (error as WorkflowExecutionError).stepExecutions;
  return Array.isArray(steps) ? steps : [];
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * « Clôture » (mahhp 22846): pick a closure motif (`WS2_GS_SEL_MOTIF_CLOTURE`)
 * and close the claim through the `claimflow-close-claim` workflow — « même
 * fonctionnement que mise à jour dossier »: server-side `GET
 * /sinistre/{id_sin}?inline=1` → `PUT /sinistre/{id_sin}` with
 * `mouvement = "CLOSIN"` and the chosen `motif`.
 *
 * « Valider » stays disabled until a motif is chosen (the workflow refuses an
 * empty one). On success the card folds into a "Sinistre clôturé" recap that
 * keeps the chosen motif on display — a closed claim stays closed, so there is
 * no reset — and `onClaimClosed` lets the page refresh the claim summary. A
 * failed run shows its error and step trail and leaves the form editable for a
 * retry.
 *
 * Keyed on the claim: another claim (a params-only navigation) starts from a
 * fresh card — the previous claim's "closed" recap never carries over.
 */
export function ClotureSinistreCard(props: Readonly<ClotureSinistreCardProps>) {
  return <ClotureSinistreCardInner key={props.idSin} {...props} />;
}

function ClotureSinistreCardInner({
  idSin,
  onClaimClosed,
  _layoutOverrides,
}: Readonly<ClotureSinistreCardProps>) {
  // Collapsed on first paint — every card in the claim view's right column
  // opens folded. Driven by state + `onExpandChange` rather than a literal
  // `defaultExpanded={false}`, because `Card` re-applies a literal default on
  // every parent re-render, which would fold the card again mid-edit.
  const [expanded, setExpanded] = useState(false);

  const [motifs, setMotifs] = useState<MotifClotureOption[]>([]);
  const [optionsState, setOptionsState] = useState<OptionsLoadState>('loading');
  const [optionsError, setOptionsError] = useState<string | null>(null);

  const [submitted, setSubmitted] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [failedSteps, setFailedSteps] = useState<WorkflowStepExecutionSummary[]>([]);

  // -- Options -------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;
    // Async loader so the setState calls are not synchronous statements in
    // the effect body (react-hooks/set-state-in-effect).
    async function loadMotifs(): Promise<void> {
      try {
        const rows = await clotureService.fetchMotifsCloture();
        if (cancelled) return;
        setMotifs(rows);
        setOptionsState('ready');
      } catch (e) {
        if (cancelled) return;
        setMotifs([]);
        setOptionsState('error');
        setOptionsError(
          e instanceof Error && e.message !== ''
            ? e.message
            : CLOTURE_SINISTRE_TEXT.optionsLoadErrorFallback,
        );
      }
    }
    void loadMotifs();
    return () => {
      cancelled = true;
    };
  }, []);

  // -- Form ----------------------------------------------------------------

  const baseConfig = useMemo(() => buildClotureSinistreFormConfig(motifs), [motifs]);

  usePublishLayoutConfig(
    CLOTURE_SINISTRE_COMPONENT_ID,
    buildPublishedConfigFromEntityForm(baseConfig, CLOTURE_SINISTRE_TEXT.title),
  );

  const mergedConfig = useMemo(
    () => applyLayoutOverrides(baseConfig, _layoutOverrides),
    [baseConfig, _layoutOverrides],
  );

  const formConfig = useMemo(
    () => withRuntimeState(mergedConfig, optionsState, submitted),
    [mergedConfig, optionsState, submitted],
  );

  const mutationFn = useCallback(
    (payload: Record<string, unknown> | FormData) => {
      setCloseError(null);
      setFailedSteps([]);
      const motif = readMotif(payload);
      // Never send an empty motif — the button is disabled without one anyway.
      if (motif === null) {
        return Promise.reject(new Error(CLOTURE_SINISTRE_TEXT.motifMissing));
      }
      return clotureService.closeClaim(idSin, motif);
    },
    [idSin],
  );

  const handleSuccess = useCallback(() => {
    setSubmitted(true);
    // Folded recap, like the other right-column cards after their action.
    setExpanded(false);
    // The page re-fetches the claim summary so the Résumé status refreshes.
    onClaimClosed?.();
  }, [onClaimClosed]);

  const handleError = useCallback((e: unknown) => {
    setCloseError(
      e instanceof Error && e.message !== '' ? e.message : CLOTURE_SINISTRE_TEXT.closeErrorFallback,
    );
    setFailedSteps(readStepExecutions(e));
  }, []);

  const form = useEntityForm({
    config: formConfig,
    mutationFn,
    onSuccess: handleSuccess,
    onError: handleError,
  });

  // « Valider » is gated on the live value of the stable key: `form.isValid`
  // is derived from the errors map, which is empty before any field is
  // touched, so it cannot drive a disabled-on-mount button.
  const hasMotif = toMotifCode(form.getValue(MOTIF_FIELD_KEY)) !== null;

  // -- Render --------------------------------------------------------------

  const title = resolveCardTitle(CLOTURE_SINISTRE_TEXT.title, _layoutOverrides);
  const submitLabel = formConfig.actions?.submit?.label ?? CLOTURE_SINISTRE_TEXT.submit;

  return (
    <Card id={actionAnchorId(CLOTURE_SINISTRE_COMPONENT_ID)}>
      <Card.Header collapsible defaultExpanded={expanded} onExpandChange={setExpanded}>
        <Icon
          icon={submitted ? CheckCircle2 : Lock}
          size="md"
          color={submitted ? 'success' : 'primary'}
        />
        <Text as="span" weight="semibold" size="base">
          {title}
        </Text>
        {submitted && (
          <Text as="span" size="sm" color="muted" className="ml-2">
            ({CLOTURE_SINISTRE_TEXT.submitted})
          </Text>
        )}
      </Card.Header>
      <Card.Content>
        {optionsError && (
          <div className="pb-4">
            <Alert variant="error" title={CLOTURE_SINISTRE_TEXT.optionsLoadErrorTitle}>
              {optionsError}
            </Alert>
          </div>
        )}
        <EntityForm config={formConfig} form={form}>
          <EntityForm.Fields />
          {closeError && (
            <div className="pt-4">
              <Alert variant="error" title={CLOTURE_SINISTRE_TEXT.closeErrorTitle}>
                {closeError}
              </Alert>
            </div>
          )}
          {failedSteps.length > 0 && (
            <div className="pt-4">
              <Text size="sm" weight="semibold" className="mb-2">
                {CLOTURE_SINISTRE_TEXT.stepsHeading}
              </Text>
              <WorkflowStepTracker steps={failedSteps} />
            </div>
          )}
          {submitted ? (
            <div className="pt-4">
              <Button variant="primary" disabled className="w-full">
                {CLOTURE_SINISTRE_TEXT.submitted}
              </Button>
            </div>
          ) : (
            formConfig.actions?.submit?.visible !== false && (
              <div className="pt-4">
                <Button
                  type="submit"
                  variant="primary"
                  loading={form.isSubmitting}
                  disabled={form.isSubmitting || !hasMotif || optionsState !== 'ready'}
                  className="w-full"
                >
                  {submitLabel}
                </Button>
              </div>
            )
          )}
        </EntityForm>
      </Card.Content>
    </Card>
  );
}
